import { useEffect, useMemo, useRef } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import Icon from '@/components/snippets/icon/icon'
import { useOption } from 'libs/athos/hooks/useOption'

const PAGES_TO_SHOW_COUNT = 3

/**
 * Numbered pagination control. Defaults to reading/writing page state via the
 * Athos context (search/collection grids). Pass activePage/totalPages/
 * onPageChange to drive it from local state instead (e.g. the blog listing,
 * which has no Athos provider).
 */
export function Pagination({ activePage: activePageProp, totalPages: totalPagesProp, onPageChange } = {}) {
  const isControlled = onPageChange != null
  const context = isControlled ? null : useAthosContext()

  const contextActivePage = isControlled ? null : useOption('page', {
    defaultValue: 1,
    syncWithURL: true
  })

  const activePage = isControlled ? activePageProp : contextActivePage
  const totalPages = isControlled ? (totalPagesProp ?? 1) : (context?.results?.pagination?.totalPages ?? 1)

  const pagesToShow = useMemo(() => {
    const pageNumbers = Array.from(Array(totalPages)).map((_, i) => i + 1)

    if (totalPages < PAGES_TO_SHOW_COUNT) return pageNumbers
    if (activePage < PAGES_TO_SHOW_COUNT) return pageNumbers.slice(0, PAGES_TO_SHOW_COUNT)
    if (activePage > totalPages - PAGES_TO_SHOW_COUNT) return pageNumbers.slice(-PAGES_TO_SHOW_COUNT)
    return pageNumbers.slice(
      activePage - Math.floor(PAGES_TO_SHOW_COUNT / 2),
      activePage + Math.ceil(PAGES_TO_SHOW_COUNT / 2)
    )
  }, [activePage, totalPages])

  // Scroll to top when the page changes, but not on the initial mount so we
  // don't fight scroll-restore on a deep-linked ?page=N load.
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, [activePage])

  const handleLoadPage = (page) => {
    if (isControlled) onPageChange(page)
    else context.refine('page', page)
  }
  const handleNextPage = () => handleLoadPage(activePage + 1)
  const handlePrevPage = () => handleLoadPage(activePage - 1)

  return (
    <div className="flex flex-row border border-border rounded-none">
      <button
        className="group text-xs leading-none size-2.5 relative aspect-square transition-colors hover:bg-type-black-primary disabled:opacity-30 disabled:pointer-events-none"
        onClick={handlePrevPage}
        disabled={activePage <= 1}
        aria-label="Previous"
      >
        <div className="size-1 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 children:size-full text-type-black-primary transition-colors group-hover:text-type-white-primary">
          <Icon id="chevron-left" />
        </div>
        <span className="sr-only">Previous</span>
      </button>
      {pagesToShow.map((page) => (
        <button
          key={page}
          disabled={page === activePage}
          className="group text-xs leading-none size-2.5 relative aspect-square transition-colors hover:bg-type-black-primary disabled:bg-type-black-primary"
          onClick={() => handleLoadPage(page)}
          aria-label={`Page ${page}`}
        >
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-type-black-primary transition-colors group-hover:text-type-white-primary group-disabled:text-type-white-primary">{page}</span>
        </button>
      ))}
      <button
        className="group text-xs leading-none size-2.5 relative aspect-square transition-colors hover:bg-type-black-primary disabled:opacity-30 disabled:pointer-events-none"
        onClick={handleNextPage}
        disabled={activePage === totalPages}
        aria-label="Next"
      >
        <span className="sr-only">Next</span>
        <div className="size-1 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 children:size-full text-type-black-primary transition-colors group-hover:text-type-white-primary">
          <Icon id="chevron-right" />
        </div>
      </button>
    </div>
  )
}
